import { useContext, useState, useEffect, useCallback } from 'react';
import { MyContext } from '../../Context/MyProvider';
import { Link } from 'react-router-dom';

const ROOT = () => document.getElementById('root') || document.body;

const WelcomeBanner = () => {
    const {
        signupBonusStatus, signupBonusTokens, signupBonusValidity,
        signupAiBonusStatus, signupAiBonusTokens, signupAiBonusValidity,
        isLoginAuth
    } = useContext(MyContext);

    const [dismissed, setDismissed] = useState(false);
    const [visible, setVisible] = useState(false);
    const [tick, setTick] = useState(0);

    const hasModuleBonus = signupBonusStatus === 1;
    const hasAiBonus = signupAiBonusStatus === 1;
    const showBanner = (hasModuleBonus || hasAiBonus) && !dismissed && isLoginAuth;

    // Add/remove banner-offset class on root so header shifts correctly
    useEffect(() => {
        const root = ROOT();
        if (showBanner) {
            root.classList.add('banner-offset');
        } else {
            root.classList.remove('banner-offset');
        }
        return () => root.classList.remove('banner-offset');
    }, [showBanner]);

    // Animate in
    useEffect(() => {
        if (showBanner) {
            const t = setTimeout(() => setVisible(true), 100);
            return () => clearTimeout(t);
        }
    }, [showBanner]);

    // Ticker animation for cycling bonuses
    useEffect(() => {
        if (!showBanner) return;
        const msgs = [];
        if (hasModuleBonus) msgs.push(0);
        if (hasAiBonus) msgs.push(1);
        if (msgs.length < 2) return;
        const interval = setInterval(() => setTick(t => t + 1), 4000);
        return () => clearInterval(interval);
    }, [showBanner, hasModuleBonus, hasAiBonus]);

    const handleDismiss = useCallback(() => {
        setVisible(false);
        ROOT().classList.remove('banner-offset');
        setTimeout(() => setDismissed(true), 500);
    }, []);

    if (!showBanner) return null;

    // Build messages
    const messages = [];
    if (hasModuleBonus) messages.push({
        icon: '🎁',
        text: `New users get <strong>${(signupBonusTokens || 0).toLocaleString()} free Module tokens</strong> for ${signupBonusValidity} days — Sign up now!`,
        cta: 'Get Started',
    });
    if (hasAiBonus) messages.push({
        icon: '✨',
        text: `New users get <strong>${(signupAiBonusTokens || 0).toLocaleString()} free AI Chat tokens</strong> for ${signupAiBonusValidity} days — Join today!`,
        cta: 'Join Free',
    });

    const currentMsg = messages[tick % messages.length];

    return (
        <div
            style={{
                transform: visible ? 'translateY(0)' : 'translateY(-100%)',
                transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
            className="w-full z-[999] relative overflow-hidden"
        >
            {/* Gradient background */}
            <div className="relative bg-gradient-to-r from-[#4f46e5] via-[#7c3aed] to-[#db2777] py-3 px-4">

                {/* Animated shimmer overlay */}
                <div
                    className="absolute inset-0 opacity-20"
                    style={{
                        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
                        animation: 'bannerShimmer 3s ease-in-out infinite',
                    }}
                />

                {/* Floating sparkles */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    {[...Array(6)].map((_, i) => (
                        <div
                            key={i}
                            className="absolute text-white/30 text-[10px]"
                            style={{
                                left: `${15 + i * 15}%`,
                                top: `${20 + (i % 2) * 40}%`,
                                animation: `float ${2 + i * 0.3}s ease-in-out infinite alternate`,
                                animationDelay: `${i * 0.4}s`,
                            }}
                        >
                            ✦
                        </div>
                    ))}
                </div>

                {/* Content */}
                <div className="relative flex items-center justify-center gap-3 text-white text-[13px] font-medium max-w-5xl mx-auto">

                    {/* Pulsing badge */}
                    <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider shrink-0">
                        <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse inline-block" />
                        Limited Offer
                    </div>

                    {/* Message (animated transition) */}
                    <p
                        key={tick}
                        className="text-center leading-snug"
                        style={{ animation: 'fadeSlideUp 0.5s ease both' }}
                        dangerouslySetInnerHTML={{
                            __html: `${currentMsg.icon} ${currentMsg.text}`
                        }}
                    />

                    {/* CTA Button */}
                    <Link
                        to="/auth/login"
                        className="shrink-0 bg-white text-[#4f46e5] text-[12px] font-bold px-3 py-1 rounded-full hover:bg-yellow-300 hover:text-black transition-all duration-300 shadow-md whitespace-nowrap"
                    >
                        {currentMsg.cta} →
                    </Link>

                    {/* Dismiss */}
                    <button
                        onClick={handleDismiss}
                        className="shrink-0 ml-1 text-white/60 hover:text-white transition-colors duration-200 p-0.5"
                        aria-label="Dismiss"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes bannerShimmer {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(200%); }
                }
                @keyframes float {
                    from { transform: translateY(0px) rotate(0deg); }
                    to { transform: translateY(-6px) rotate(20deg); }
                }
                @keyframes fadeSlideUp {
                    from { opacity: 0; transform: translateY(6px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default WelcomeBanner;
